import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Card,
  CircularProgress,
  Container,
  Grid,
  Typography
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { UserContext } from 'src/contexts/userContext';
import AccessContextProvider from 'src/contexts/AccessContextProvider';
import Access from '../ManageAccess/Access';
import Log from 'src/content/dashboards/Log/Log';
import { TimeSpan, UnixTime } from 'src/dataCache/time';
import { FetchResult } from 'src/dataCache/dataCache';
import { JSONRecordData } from 'src/content/dashboards/Log/graph.util';
import { FormattedMessage } from 'react-intl';
import Overview from '../Overview/overview';
import WeeklyReport from '../SodiohomeInstallations/WeeklyReport';
import { fetchDataJson } from 'src/content/dashboards/Installations/fetchData';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import Information from '../Information/Information';
import { UserType } from '../../../interfaces/UserTypes';
import { PARTNER_TICKETS_ENABLED } from '../../../config/featureFlags';
import HistoryOfActions from '../History/History';
import Topology from '../Topology/Topology';
import TopologySodistoreGrid from '../Topology/TopologySodistoreGrid';
import BatteryView from '../BatteryView/BatteryView';
import Configuration from '../Configuration/Configuration';
import ConfigurationSodistoreGrid from '../Configuration/ConfigurationSodistoreGrid';
import PvView from '../PvView/PvView';
import InstallationTicketsTab from '../Tickets/InstallationTicketsTab';
import DocumentsTab from '../Documents/DocumentsTab';
import InstallationChecklistTab from '../Checklist/InstallationChecklistTab';
import { CHECKLIST_ENABLED_PRODUCTS } from 'src/interfaces/ChecklistTypes';

interface singleInstallationProps {
  current_installation?: I_Installation;
  type?: string;
}

function Installation(props: singleInstallationProps) {
  if (props.current_installation == undefined) {
    return null;
  }
  const context = useContext(UserContext);
  const { currentUser } = context;
  const location = useLocation().pathname;
  const [fetchFunctionCalled, setFetchFunctionCalled] = useState(false);
  const [errorLoadingS3Data, setErrorLoadingS3Data] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>(undefined);
  const [values, setValues] = useState<JSONRecordData | null>(null);
  const status = props.current_installation.status;
  // For SodiStoreGrid (product 4), backend heartbeat path is broken (SinexcelCommunication
  // hardcodes Product=2), so installation.status stays -1 even when S3 is fresh.
  // TODO: remove this override once SinexcelCommunication/Program.cs derives Product from runtime config.
  // Treat as Green when our S3 fetch succeeded (connected === true).
  const [connected, setConnected] = useState(true);
  const [loading, setLoading] = useState(true);

  const S3data = {
    s3Region: props.current_installation.s3Region,
    s3Provider: props.current_installation.s3Provider,
    s3Key: props.current_installation.s3Key,
    s3Secret: props.current_installation.s3Secret,
    s3BucketId: props.current_installation.s3BucketId
  };

  // TODO: SodistoreGrid — uses its own bucket salt
  const s3BucketSalt =
    props.current_installation.product === 4
      ? '5109c126-e141-43ab-8658-f3c44c838ae8'
      : '3e5b3069-214a-43ee-8d85-57d72000c19d';
  const s3Bucket =
    props.current_installation.s3BucketId.toString() + '-' + s3BucketSalt;

  const s3Credentials = { s3Bucket, ...S3data };

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay));
  }

  //In React, useRef creates a mutable object that persists across renders without triggering re-renders when its value changes.
  //While fetching, we check the value of continueFetching, if its false, we break. This means that either the user changed tab or the object has been finished its execution (return)
  const continueFetching = useRef(false);

  // SodiStoreGrid (Sinexcel) uploads chunked files every ~150s with arbitrary
  // unix-second filenames (read key denies LIST — only GET is allowed).
  // Probe a wide window in parallel batches with 1-second step.
  // `checkContinue` lets the loop short-circuit when the user leaves Live,
  // bounding wasted in-flight GETs to a single 20-request batch.
  const probeLatestGridChunk = async (
    maxAgeSeconds: number = 600,
    checkContinue: () => boolean = () => true
  ): Promise<{ res: any; ts: any } | null> => {
    const batchSize = 20;
    const step = 1;
    for (let batchStart = 0; batchStart < maxAgeSeconds; batchStart += batchSize * step) {
      if (!checkContinue()) return null;
      const offsets: number[] = [];
      for (let j = 0; j < batchSize; j++) {
        const offset = batchStart + j * step;
        if (offset < maxAgeSeconds) offsets.push(offset);
      }
      const now = UnixTime.now();
      const results = await Promise.all(
        offsets.map(async (offset) => {
          const ts = now.earlier(TimeSpan.fromSeconds(offset));
          const r = await fetchDataJson(ts, s3Credentials, false);
          return r !== FetchResult.notAvailable && r !== FetchResult.tryLater
            ? { res: r, ts }
            : null;
        })
      );
      const hit = results.find((r) => r !== null);
      if (hit) return hit;
    }
    return null;
  };

  const fetchDataForOneTime = async () => {
    var timeperiodToSearch = 70;
    let res;
    let timestampToFetch;

    if (props.current_installation.product === 4) {
      const hit = await probeLatestGridChunk(600);
      if (!hit) {
        setConnected(false);
        setLoading(false);
        return false;
      }
      setConnected(true);
      setLoading(false);
      const timestamps = Object.keys(hit.res).sort(
        (a, b) => Number(a) - Number(b)
      );
      setValues(hit.res[timestamps[timestamps.length - 1]]);
      return true;
    }

    for (var i = timeperiodToSearch; i > 0; i -= 2) {
      timestampToFetch = UnixTime.now().earlier(TimeSpan.fromSeconds(i));
      try {
        res = await fetchDataJson(timestampToFetch, s3Credentials, false);
        if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
          break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        return false;
      }
    }

    if (i <= 0) {
      setConnected(false);
      setLoading(false);
      return false;
    }
    setConnected(true);
    setLoading(false);

    const timestamp = Object.keys(res)[Object.keys(res).length - 1];
    setValues(res[timestamp]);
    // setValues(
    //   extractValues({
    //     time: UnixTime.fromTicks(parseInt(timestamp, 10)),
    //     value: res[timestamp]
    //   })
    // );
    return true;
  };

  const fetchDataPeriodically = async () => {
    var timeperiodToSearch = 200;
    let res;
    let timestampToFetch;

    // SodiStoreGrid: probe a wide window in parallel (read key denies LIST),
    // stream through that chunk's timestamps, then refresh.
    // Backoff schedule on consecutive misses to cap S3 cost on offline installs.
    if (props.current_installation.product === 4) {
      let firstHit = false;
      let consecutiveMisses = 0;
      const backoffMs = [30000, 60000, 120000, 300000]; // 30s → 60s → 2m → 5m (cap)
      while (continueFetching.current) {
        // Narrow window after first hit; widen back if we lose it.
        const window = firstHit && consecutiveMisses === 0 ? 200 : 600;
        const hit = await probeLatestGridChunk(
          window,
          () => continueFetching.current
        );
        if (!continueFetching.current) break;
        if (!hit) {
          consecutiveMisses += 1;
          // Always reflect disconnection in the UI — even after a prior hit,
          // so the user sees a stale-data signal instead of a frozen chart.
          setConnected(false);
          if (!firstHit) setLoading(false);
          const wait = backoffMs[Math.min(consecutiveMisses - 1, backoffMs.length - 1)];
          await timeout(wait);
          continue;
        }
        consecutiveMisses = 0;
        if (!firstHit) {
          firstHit = true;
          setLoading(false);
        }
        setConnected(true);
        // Stream through chunk timestamps in ascending order (chunk = ~15 records, ~10s apart)
        const orderedTs = Object.keys(hit.res).sort(
          (a, b) => Number(a) - Number(b)
        );
        for (const t of orderedTs) {
          if (!continueFetching.current) {
            setFetchFunctionCalled(false);
            return false;
          }
          setValues(hit.res[t]);
          await timeout(2000);
        }
        await timeout(30000);
      }
      setFetchFunctionCalled(false);
      return false;
    }

    for (var i = 0; i < timeperiodToSearch; i += 2) {
      if (!continueFetching.current) {
        return false;
      }
      timestampToFetch = UnixTime.now().earlier(TimeSpan.fromSeconds(i));

      try {
        res = await fetchDataJson(timestampToFetch, s3Credentials, false);
        if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
          break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        return false;
      }
    }

    if (i >= timeperiodToSearch) {
      setConnected(false);
      setLoading(false);
      return false;
    }
    setConnected(true);
    setLoading(false);

    while (continueFetching.current) {
      for (const timestamp of Object.keys(res)) {
        if (!continueFetching.current) {
          setFetchFunctionCalled(false);
          return false;
        }
        console.log(`Timestamp: ${timestamp}`);
        const { Config: { S3: { Key, Secret, ...s3Rest } = {} as any, ...configRest } = {} as any, ...dataRest } = res[timestamp] || {};
        console.log({ ...dataRest, Config: { ...configRest, S3: { ...s3Rest, Key: '***', Secret: '***' } } });

        setValues(res[timestamp]);
        await timeout(2000);
      }

      timestampToFetch = timestampToFetch.later(TimeSpan.fromSeconds(60));
      console.log('NEW TIMESTAMP TO FETCH IS ' + timestampToFetch);

      for (i = 0; i < 30; i++) {
        if (!continueFetching.current) {
          return false;
        }

        try {
          console.log('Trying to fetch timestamp ' + timestampToFetch);
          res = await fetchDataJson(timestampToFetch, s3Credentials, false);
          if (
            res !== FetchResult.notAvailable &&
            res !== FetchResult.tryLater
          ) {
            break;
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          return false;
        }
        timestampToFetch = timestampToFetch.later(TimeSpan.fromSeconds(1));
      }
      if (i == 30) {
        return false;
      }
    }
  };
  useEffect(() => {
    let path = location.split('/');
    setCurrentTab(path[path.length - 1]);
  }, [location]);

  //If status is -1, the  topology will be empty and "Unable to communicate with the installation" should be rendered from the Topology component.
  //SodiStoreGrid (product 4) is excluded: backend heartbeat path is broken
  //because SinexcelCommunication hardcodes Product=2 — trust S3 freshness instead.
  useEffect(() => {
    if (status === -1 && props.current_installation.product !== 4) {
      setConnected(false);
    }
  }, [status, props.current_installation.product]);

  useEffect(() => {
    if (
      currentTab == 'live' ||
      currentTab == 'pvview' ||
      currentTab == 'configuration' ||
      currentTab == 'overview' ||
      location.includes('batteryview')
    ) {
      //Fetch periodically if the tab is live, pvview or batteryview
      if (
        currentTab == 'live' ||
        currentTab == 'pvview' ||
        (location.includes('batteryview') && !location.includes('mainstats'))
      ) {
        if (!continueFetching.current) {
          continueFetching.current = true;
          //Call the function only one time. When the location and the currentTab change, this useEffect will be called 2 times
          if (!fetchFunctionCalled) {
            setFetchFunctionCalled(true);
            fetchDataPeriodically();
          }
        }
      }
      // Fetch one time in overview tab to determine connectivity
      if (currentTab == 'overview') {
        fetchDataForOneTime();
      }
      //Fetch only one time in configuration tab
      if (currentTab == 'configuration') {
        fetchDataForOneTime();
      }

      return () => {
        continueFetching.current = false;
      };
    } else {
      //If the tab is not live, pvview, batteryview or configuration, then stop fetching.
      continueFetching.current = false;
    }
  }, [currentTab, location]);

  return (
    <>
      <Grid item xs={12} md={12}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            <FormattedMessage
              id="installation_name_simple"
              defaultMessage="Installation Name:"
            />
          </Typography>
          <Typography
            fontWeight="bold"
            color="orange"
            noWrap
            sx={{
              marginTop: '-20px',
              marginBottom: '10px',
              marginLeft: '5px',
              fontSize: '14px'
            }}
          >
            {props.current_installation.name}
          </Typography>
        </div>

        {currentTab == 'live' && values && values.EssControl?.Mode && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              fontWeight="bold"
              color="text.primary"
              noWrap
              sx={{
                marginTop: '0px',
                marginBottom: '10px',
                fontSize: '14px'
              }}
            >
              <FormattedMessage id="mode" defaultMessage="Mode:" />
            </Typography>
            <Typography
              fontWeight="bold"
              color="orange"
              noWrap
              sx={{
                marginTop: '0px',
                marginBottom: '10px',
                marginLeft: '85px',
                fontSize: '14px'
              }}
            >
              {values.EssControl.Mode}
            </Typography>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{
              marginTop: '0px',
              marginBottom: '10px',
              fontSize: '14px'
            }}
          >
            Status:
          </Typography>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '75px',
              marginTop: '-10px'
            }}
          >
            {status === -1 && !(props.current_installation.product === 4 && connected) ? (
              <CancelIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: 'red',
                  borderRadius: '50%'
                }}
              />
            ) : (
              ''
            )}

            {status === -2 ? (
              <CircularProgress
                size={20}
                sx={{
                  color: '#f7b34d'
                }}
              />
            ) : (
              ''
            )}

            <div
              style={{
                width: '20px',
                height: '20px',
                marginLeft: '2px',
                borderRadius: '50%',
                backgroundColor:
                  status === 2
                    ? 'red'
                    : status === 1
                    ? 'orange'
                    : (status === -1 || status === -2) &&
                      !(props.current_installation.product === 4 && connected)
                    ? 'transparent'
                    : 'green'
              }}
            />

            {props.current_installation.testingMode && (
              <BuildIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: 'purple',
                  borderRadius: '50%',
                  position: 'relative',
                  zIndex: 1,
                  marginLeft: '15px'
                }}
              />
            )}
          </div>
        </div>

        {loading &&
          currentTab != 'information' &&
          currentTab != 'history' &&
          // currentTab != 'manage' &&
          currentTab != 'log' &&
          currentTab != 'report' &&
          currentTab != 'installationTickets' &&
          currentTab != 'checklist' && (
            <Container
              maxWidth="xl"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '70vh'
              }}
            >
              <CircularProgress size={60} style={{ color: '#ffc04d' }} />
              <Typography
                variant="body2"
                style={{ color: 'black', fontWeight: 'bold' }}
                mt={2}
              >
                <FormattedMessage id="connectingToDevice" defaultMessage="Connecting to the device..." />
              </Typography>
            </Container>
          )}

        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.information}
                element={
                  <Information
                    values={props.current_installation}
                    s3Credentials={s3Credentials}
                    type={props.type}
                  ></Information>
                }
              />

              <Route
                path={routes.batteryview + '/*'}
                element={
                  <BatteryView
                    values={values}
                    s3Credentials={s3Credentials}
                    installationId={props.current_installation.id}
                    connected={connected}
                  ></BatteryView>
                }
              ></Route>

              {/* TODO: SodistoreGrid — PV View excluded, add back when data path is ready */}
              {props.current_installation.product !== 4 && (
                <Route
                  path={routes.pvview + '/*'}
                  element={
                    <PvView values={values} connected={connected}></PvView>
                  }
                ></Route>
              )}

              <Route
                path={routes.overview}
                element={
                  <Overview
                    s3Credentials={s3Credentials}
                    id={props.current_installation.id}
                    connected={connected}
                    loading={loading}
                  ></Overview>
                }
              />

              {/* SodistoreMax (product 3) reuses the SodistoreHome energy report UI;
                  daily rows are ingested from S3 snapshots by SodiStoreMaxEnergyParser.
                  Hidden when data collection is off (no data + avoids wasted S3 calls). */}
              {props.current_installation.product === 3 &&
                props.current_installation.dataCollectionEnabled !== false && (
                <Route
                  path={routes.report}
                  element={
                    <WeeklyReport
                      installationId={props.current_installation.id}
                      installationName={props.current_installation.name}
                      installationEmail={props.current_installation.email}
                      showEmail={false}   // SodistoreMax: no email for this product (hide auto-send + Send Report)
                      showRefresh={true}  // Refresh (force re-ingest) — recovers daily days missing after a write outage
                      showHourly={false}  // SodistoreMax has no hourly data (integrated daily totals only)
                    />
                  }
                />
              )}

              <Route
                path={routes.live}
                element={
                  props.current_installation.product === 4 ? (
                    <TopologySodistoreGrid
                      values={values}
                      connected={connected}
                      loading={loading}
                    ></TopologySodistoreGrid>
                  ) : (
                    <Topology
                      values={values}
                      connected={connected}
                      loading={loading}
                    ></Topology>
                  )
                }
              />

              <Route
                path={routes.log}
                element={
                  <Log
                    errorLoadingS3Data={errorLoadingS3Data}
                    id={props.current_installation.id}
                    status={props.current_installation.status}
                  ></Log>
                }
              />

              {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.configuration}
                  element={
                    props.current_installation.product === 4 ? (
                      <ConfigurationSodistoreGrid
                        values={values}
                        id={props.current_installation.id}
                      />
                    ) : (
                      <Configuration
                        values={values}
                        id={props.current_installation.id}
                      ></Configuration>
                    )
                  }
                />
              )}
              {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.history}
                  element={
                    <HistoryOfActions
                      errorLoadingS3Data={errorLoadingS3Data}
                      id={props.current_installation.id}
                    ></HistoryOfActions>
                  }
                />
              )}

              {/* {currentUser.userType == UserType.admin && (
                <Route
                  path={routes.manage}
                  element={
                    <AccessContextProvider>
                      <Access
                        currentResource={props.current_installation}
                        resourceType={props.type}
                      ></Access>
                    </AccessContextProvider>
                  }
                />
              )} */}

              {(currentUser.userType == UserType.admin ||
                PARTNER_TICKETS_ENABLED) && (
                <Route
                  path={routes.installationTickets}
                  element={
                    <InstallationTicketsTab
                      installationId={props.current_installation.id}
                    />
                  }
                />
              )}

              {/* Documents tab is open to every role: each sees the installation's
                  files and may delete the ones they uploaded themselves. */}
              {(currentUser.userType == UserType.admin ||
                currentUser.userType == UserType.partner ||
                currentUser.userType == UserType.client) && (
                <Route
                  path={routes.documents}
                  element={
                    <DocumentsTab
                      installationId={props.current_installation.id}
                      product={props.current_installation.product}
                    />
                  }
                />
              )}

              {currentUser.userType == UserType.admin &&
                CHECKLIST_ENABLED_PRODUCTS.has(props.current_installation.product) && (
                  <Route
                    path={routes.checklist}
                    element={
                      <InstallationChecklistTab
                        installationId={props.current_installation.id}
                      />
                    }
                  />
                )}

              <Route
                path={'*'}
                element={<Navigate to={routes.live}></Navigate>}
              />
            </Routes>
          </Grid>
        </Card>
      </Grid>
    </>
  );
}

export default Installation;
